
import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Plus, TrendingUp, Users, Heart } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { useToast } from "@/hooks/use-toast";
import { Layout } from "@/components/Layout";
import { supabase } from "@/integrations/supabase/client";
import { Group } from "@/lib/types";
import { useNavigate } from "react-router-dom";
import { GroupSearch } from "@/components/groups/GroupSearch";
import { GroupFilters } from "@/components/groups/GroupFilters";
import { GroupList } from "@/components/groups/GroupList";
import { useQueryClient } from "@tanstack/react-query";

const Groups = () => {
  const [groups, setGroups] = useState<Group[]>([]);
  const [filteredGroups, setFilteredGroups] = useState<Group[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [newGroup, setNewGroup] = useState({
    name: "",
    description: "",
    topics: "",
    is_private: false,
    category: "other"
  });
  const { toast } = useToast();
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);
  const [userRoles, setUserRoles] = useState<Record<string, string>>({});
  const navigate = useNavigate();
  const [showTrending, setShowTrending] = useState(false);
  const [showMyGroups, setShowMyGroups] = useState(false);
  const [myGroupIds, setMyGroupIds] = useState<string[]>([]);
  const queryClient = useQueryClient();

  useEffect(() => {
    const checkAuth = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate("/auth");
        return;
      }
      setIsAuthenticated(true);
      fetchGroups();
      fetchMyMemberships();
    };

    checkAuth();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (!session) {
        navigate("/auth");
        return;
      }
      setIsAuthenticated(true);
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchMyMemberships = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: memberships, error } = await supabase
        .from('group_members')
        .select('group_id')
        .eq('user_id', user.id);

      if (error) {
        console.error('Error fetching memberships:', error);
        return;
      }

      setMyGroupIds(memberships.map(m => m.group_id));
    } catch (error) {
      console.error('Error in fetchMyMemberships:', error);
    }
  };

  const fetchGroups = async () => {
    try {
      setError(null);
      setIsLoading(true);
      
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        setError("Please sign in to view groups");
        return;
      }

      let query = supabase
        .from('groups')
        .select(`
          *,
          profiles:created_by (
            username,
            avatar_url
          )
        `);

      if (selectedCategory !== 'all') {
        query = query.eq('category', selectedCategory as any);
      }

      if (searchQuery) {
        query = query.textSearch('search_vector', searchQuery);
      }

      if (showTrending) {
        query = query.order('trending_score', { ascending: false }).limit(10);
      } else {
        query = query.order('created_at', { ascending: false });
      }

      const { data: groupsData, error: groupsError } = await query;

      if (groupsError) throw groupsError;

      const { data: roles, error: rolesError } = await supabase
        .from('group_roles')
        .select('group_id, role')
        .eq('user_id', user.id);

      if (rolesError) throw rolesError;

      const roleMap: Record<string, string> = {};
      roles?.forEach(role => {
        roleMap[role.group_id] = role.role;
      });

      let filteredData = groupsData || [];
      
      // Apply "My Groups" filter if enabled
      if (showMyGroups && myGroupIds.length > 0) {
        filteredData = filteredData.filter(group => myGroupIds.includes(group.id));
      }

      setGroups(groupsData || []);
      setFilteredGroups(filteredData);
      setUserRoles(roleMap);
    } catch (error: any) {
      console.error('Error:', error);
      setError(error.message);
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive",
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleJoinGroup = async (groupId: string) => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to join groups",
        variant: "destructive",
      });
      return;
    }

    try {
      console.log("handleJoinGroup called for group:", groupId);
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        console.log("No user found");
        return;
      }

      // Check if the user is already a member
      const { data: existingMember } = await supabase
        .from('group_members')
        .select('*')
        .eq('group_id', groupId)
        .eq('user_id', user.id)
        .maybeSingle();
      
      if (existingMember) {
        console.log("User is already a member of this group");
        toast({
          title: "Already a member",
          description: "You're already a member of this group.",
        });
        return;
      }

      console.log("Inserting new group member");
      const { error: insertError } = await supabase
        .from('group_members')
        .insert({ 
          group_id: groupId, 
          user_id: user.id 
        });
        
      if (insertError) {
        console.error("Error joining group:", insertError);
        throw insertError;
      }

      console.log("Successfully joined group, now updating member count");
      const { data: groupData, error: getCountError } = await supabase
        .from('groups')
        .select('members_count')
        .eq('id', groupId)
        .single();
        
      if (getCountError) {
        console.error("Error getting member count:", getCountError);
        throw getCountError;
      }
        
      const currentCount = groupData?.members_count || 0;
      
      const { error: updateError } = await supabase
        .from('groups')
        .update({ members_count: currentCount + 1 })
        .eq('id', groupId);

      if (updateError) {
        console.error("Error updating member count:", updateError);
        throw updateError;
      }

      console.log("Group join successful, refreshing data");
      await fetchGroups();
      
      queryClient.invalidateQueries({ queryKey: ['groupMembership', groupId] });
      queryClient.invalidateQueries({ queryKey: ['group-members', groupId] });
      
      toast({
        title: "Joined group! 🎉",
        description: "Welcome to the community! Start engaging with other members.",
      });
    } catch (error: any) {
      console.error("Join group error:", error);
      toast({
        title: "Error joining group",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  const handleCreateGroup = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Authentication required",
        description: "Please sign in to create groups",
        variant: "destructive",
      });
      return;
    }

    try {
      if (!newGroup.name || !newGroup.description) {
        toast({
          title: "Missing information",
          description: "Please fill in all required fields.",
          variant: "destructive"
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      console.log("Creating new group with user:", user.id);
      
      // Create the group first
      const { data, error } = await supabase
        .from('groups')
        .insert({
          name: newGroup.name,
          description: newGroup.description,
          topics: newGroup.topics.split(',').map(topic => topic.trim()),
          created_by: user.id,
          is_private: newGroup.is_private,
          category: newGroup.category as any,
          members_count: 1 // Set initial count to 1 for the creator
        })
        .select()
        .single();

      if (error) {
        console.error("Error creating group:", error);
        throw error;
      }
      
      console.log("Group created:", data.id, "now adding creator as member");
      
      // Then add the creator as a member
      const { error: memberError } = await supabase
        .from('group_members')
        .insert({
          group_id: data.id,
          user_id: user.id
        });
      
      if (memberError) {
        console.error('Error adding creator as member:', memberError);
        throw memberError;
      }

      console.log("Creator successfully added as member");
      setGroups([data, ...groups]);
      setFilteredGroups([data, ...groups]);
      setNewGroup({ 
        name: "", 
        description: "", 
        topics: "", 
        is_private: false,
        category: "other"
      });
      setDialogOpen(false);
      
      toast({
        title: "Group created! 🎉",
        description: "Your new group has been created successfully.",
      });
      
      // Make sure to invalidate relevant queries
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['group-members', data.id] });
    } catch (error: any) {
      console.error("Create group error:", error);
      toast({
        title: "Error creating group",
        description: error.message,
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    fetchGroups();
  }, [searchQuery, selectedCategory, showTrending, showMyGroups, myGroupIds]);

  if (!isAuthenticated) {
    return null;
  }

  return (
    <Layout>
      {error ? (
        <div className="text-center py-16">
          <div className="max-w-md mx-auto">
            <div className="w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full flex items-center justify-center">
              <TrendingUp className="w-8 h-8 text-red-500" />
            </div>
            <p className="text-lg font-medium text-foreground mb-2">Oops! Something went wrong</p>
            <p className="text-muted-foreground mb-6">{error}</p>
            <Button 
              onClick={fetchGroups} 
              className="btn-primary px-6 py-2"
            >
              Try Again
            </Button>
          </div>
        </div>
      ) : (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 dark:from-gray-900 dark:via-gray-800 dark:to-purple-900/20">
          {/* Hero Section */}
          <div className="bg-gradient-to-r from-purple-600 via-pink-600 to-blue-600 text-white">
            <div className="container mx-auto px-4 py-16">
              <div className="max-w-4xl mx-auto text-center">
                <h1 className="text-4xl md:text-6xl font-bold mb-6 bg-gradient-to-r from-white to-blue-100 bg-clip-text text-transparent">
                  Find Your Support Community
                </h1>
                <p className="text-xl md:text-2xl mb-8 text-blue-100">
                  Connect with people who understand, share experiences, and grow together
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button size="lg" className="bg-white text-purple-600 hover:bg-blue-50 font-semibold px-8 py-3 rounded-full shadow-lg">
                        <Plus className="w-5 h-5 mr-2" />
                        Start a Positive Group
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-lg mx-4 rounded-2xl">
                      <DialogHeader className="text-center pb-4">
                        <DialogTitle className="text-2xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                          Create Your Support Space
                        </DialogTitle>
                        <p className="text-muted-foreground">Start a community where people can help each other grow</p>
                      </DialogHeader>
                      <div className="space-y-6">
                        <div>
                          <label className="text-sm font-semibold mb-2 block text-foreground">Group Name</label>
                          <Input
                            value={newGroup.name}
                            onChange={(e) => setNewGroup({ ...newGroup, name: e.target.value })}
                            placeholder="e.g., Mental Health Support, Daily Motivation"
                            className="rounded-xl border-2"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold mb-2 block text-foreground">Purpose & Description</label>
                          <Textarea
                            value={newGroup.description}
                            onChange={(e) => setNewGroup({ ...newGroup, description: e.target.value })}
                            placeholder="Describe how your group will help and support members..."
                            className="rounded-xl border-2 min-h-[100px]"
                          />
                        </div>
                        <div>
                          <label className="text-sm font-semibold mb-2 block text-foreground">Focus Areas</label>
                          <Input
                            value={newGroup.topics}
                            onChange={(e) => setNewGroup({ ...newGroup, topics: e.target.value })}
                            placeholder="mindfulness, anxiety support, motivation, friendship"
                            className="rounded-xl border-2"
                          />
                        </div>
                        <div className="flex items-center space-x-3 p-4 bg-purple-50 rounded-xl">
                          <Switch
                            checked={newGroup.is_private}
                            onCheckedChange={(checked) => setNewGroup({ ...newGroup, is_private: checked })}
                          />
                          <div>
                            <label className="text-sm font-semibold text-foreground">Private Group</label>
                            <p className="text-xs text-muted-foreground">Only invited members can join</p>
                          </div>
                        </div>
                        <Button onClick={handleCreateGroup} className="w-full btn-primary py-3 text-lg font-semibold">
                          Create Support Group
                        </Button>
                      </div>
                    </DialogContent>
                  </Dialog>
                  <p className="text-blue-100 text-sm">
                    ✨ Join thousands finding strength together
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Filters Section */}
          <div className="container mx-auto px-4 py-8">
            <div className="max-w-6xl mx-auto">
              <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 shadow-xl border border-white/20">
                <GroupSearch
                  searchQuery={searchQuery}
                  onSearchChange={setSearchQuery}
                />
                
                <div className="mt-6">
                  <GroupFilters
                    selectedCategory={selectedCategory as any}
                    onCategoryChange={(category) => setSelectedCategory(category)}
                    showTrending={showTrending}
                    onTrendingChange={setShowTrending}
                    showMyGroups={showMyGroups}
                    onMyGroupsChange={setShowMyGroups}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Groups Section */}
          <div className="container mx-auto px-4 pb-20">
            <div className="max-w-6xl mx-auto">
              {isLoading ? (
                <div className="text-center py-16">
                  <div className="inline-flex items-center justify-center w-16 h-16 bg-purple-100 rounded-full mb-4 animate-pulse">
                    <Heart className="w-8 h-8 text-purple-600" />
                  </div>
                  <p className="text-lg font-medium text-foreground">Finding amazing communities for you...</p>
                  <p className="text-muted-foreground">This will just take a moment</p>
                </div>
              ) : (
                <>
                  <div className="text-center mb-8">
                    <h2 className="text-3xl font-bold text-foreground mb-2">
                      {showMyGroups ? "Your Support Communities" : showTrending ? "Trending Support Groups" : "Available Support Groups"}
                    </h2>
                    <p className="text-muted-foreground text-lg">
                      {filteredGroups.length === 0 
                        ? "No groups found. Why not create the first one?"
                        : `${filteredGroups.length} communities ready to welcome you`
                      }
                    </p>
                  </div>
                  
                  <GroupList
                    groups={filteredGroups}
                    onJoinGroup={handleJoinGroup}
                    selectedGroup={selectedGroup}
                    setSelectedGroup={setSelectedGroup}
                    userRoles={userRoles}
                  />
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
};

export default Groups;
